/**
 * AvailabilityPicker — "usually here Tue/Thu evenings" (V2 Amethyst WU-02, R2).
 *
 * A compact day x time-of-day toggle grid, shared by the wizard (Stage 3)
 * and DatingProfileEdit's Lifestyle tab so the two surfaces never drift.
 */
import {
  AVAILABILITY_DAYS,
  AVAILABILITY_PERIODS,
  AvailabilityWindow,
  formatAvailabilitySummary,
} from '../../types/profile';
import './AvailabilityPicker.css';

interface AvailabilityPickerProps {
  value: AvailabilityWindow[];
  onChange: (windows: AvailabilityWindow[]) => void;
}

export function AvailabilityPicker({ value, onChange }: AvailabilityPickerProps) {
  const isSelected = (day: string, period: string) =>
    value.some((w) => w.day === day && w.period === period);

  const toggle = (day: AvailabilityWindow['day'], period: AvailabilityWindow['period']) => {
    if (isSelected(day, period)) {
      onChange(value.filter((w) => !(w.day === day && w.period === period)));
    } else {
      onChange([...value, { day, period }]);
    }
  };

  const summary = formatAvailabilitySummary(value);

  return (
    <div className="availability-picker">
      <p className="availability-picker__hint">
        Tap the times you're usually free to hang out — this helps people know when to say hi.
      </p>
      <div className="availability-picker__grid" role="group" aria-label="Availability windows">
        <div className="availability-picker__row availability-picker__row--header">
          <span className="availability-picker__corner" />
          {AVAILABILITY_DAYS.map((d) => (
            <span key={d.value} className="availability-picker__day-label">
              {d.label}
            </span>
          ))}
        </div>
        {AVAILABILITY_PERIODS.map((period) => (
          <div key={period.value} className="availability-picker__row">
            <span className="availability-picker__period-label">{period.label}</span>
            {AVAILABILITY_DAYS.map((day) => {
              const selected = isSelected(day.value, period.value);
              return (
                <button
                  key={day.value}
                  type="button"
                  className={`availability-picker__cell ${selected ? 'is-selected' : ''}`}
                  aria-pressed={selected}
                  aria-label={`${day.label} ${period.label}`}
                  onClick={() => toggle(day.value, period.value)}
                />
              );
            })}
          </div>
        ))}
      </div>
      <p className="availability-picker__summary">
        {summary ?? 'No windows picked yet — this stays optional.'}
      </p>
    </div>
  );
}

export default AvailabilityPicker;
